"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { parseTheme, THEME_COOKIE, type Theme } from "@/lib/theme";

const ThemeContext = createContext<{ theme: Theme; setTheme: (theme: Theme) => void } | null>(null);

export function ThemeProvider({ initialTheme, children }: { initialTheme: Theme; children: ReactNode }) {
  const [theme, setState] = useState(initialTheme);

  function applyTheme(value: Theme) {
    document.documentElement.dataset.theme = value;
    document.cookie = `${THEME_COOKIE}=${value}; path=/; max-age=31536000; SameSite=Lax`;
    setState(value);
  }

  function setTheme(value: Theme) {
    applyTheme(value);
    // The cookie is authoritative for the next server render. Storage is only
    // a cross-tab notification; blocked storage must not prevent switching.
    try { localStorage.setItem(THEME_COOKIE, value); } catch { /* Private browsing. */ }
  }

  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key === THEME_COOKIE) applyTheme(parseTheme(event.newValue));
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be inside ThemeProvider");
  return value;
}
